import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { notionCall } from '../../services/notion';
import { Search, FileText, ChevronRight, X, ExternalLink, Clock, AlertCircle, Maximize2, Minimize2, GripVertical, Sun, Moon, Monitor, Book, Calendar as CalendarIcon, CheckSquare, Link as LinkIcon, Type, Hash, List, AlignLeft, MoreHorizontal } from 'lucide-react';
import { SummarizeButton } from '../shared/SummarizeButton';
import { pushDebugLog } from '../../hooks/useDebugLogs';
// ─── Interfaces ───────────────────────────────────────────────────────────────
interface NotionDatabase {
  id: string;
  title: string;
  icon?: string;
  properties: Record<string, any>;
}
interface NotionPage {
  id: string;
  title: string;
  lastEdited?: string;
  url: string;
  properties: Record<string, any>;
  databaseId?: string;
}
interface PageDetail extends NotionPage {
  contentHtml: string;
}
type PreviewTheme = 'dark' | 'light' | 'system';
// ─── Helpers ──────────────────────────────────────────────────────────────────
/** Extract results array from API response — handles both { results: [...] } and direct arrays */
function extractResults(data: any): any[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  if (data.id && data.object) return [data];
  return [];
}
/** Deep-search for any text content in a Notion rich_text array */
function extractAnyText(arr: any): string {
  if (!arr) return '';
  if (typeof arr === 'string') return arr;
  if (Array.isArray(arr)) {
    return arr.map((item: any) => {
      if (typeof item === 'string') return item;
      return item?.plain_text || item?.text?.content || item?.name || '';
    }).filter(Boolean).join('');
  }
  if (typeof arr === 'object') {
    return arr.plain_text || arr.text?.content || arr.name || '';
  }
  return String(arr);
}
/** Extract a readable title from a Notion database object */
function parseDatabaseTitle(db: any): string {
  if (Array.isArray(db.title)) {
    const text = extractAnyText(db.title);
    if (text) return text;
  }
  if (typeof db.title === 'string' && db.title.trim()) return db.title;
  if (typeof db.name === 'string' && db.name.trim()) return db.name;
  if (db.properties) {
    for (const key in db.properties) {
      const prop = db.properties[key];
      if (prop?.type === 'title' && Array.isArray(prop.title)) {
        const text = extractAnyText(prop.title);
        if (text) return text;
      }
    }
  }
  return db.id ? `DB ${db.id.slice(0, 8)}` : 'Sin título';
}
/** Extract title from a Notion page object */
function parsePageTitle(page: any): string {
  if (page.properties) {
    for (const key in page.properties) {
      const prop = page.properties[key];
      if (prop?.type === 'title') {
        const text = extractAnyText(prop.title);
        if (text) return text;
      }
    }
  }
  if (Array.isArray(page.title)) {
    const text = extractAnyText(page.title);
    if (text) return text;
  }
  if (typeof page.title === 'string' && page.title.trim()) return page.title;
  return 'Sin título';
}
/** Extract a user-friendly error message from API error strings */
function parseApiError(error: string): string {
  try {
    const jsonMatch = error.match(/\{.*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      if (parsed.error?.message) return parsed.error.message;
      if (parsed.message) return parsed.message;
      if (typeof parsed.error === 'string') return parsed.error;
    }
  } catch {}
  if (error.startsWith('Notion MCP error')) {
    const statusMatch = error.match(/error (\d+):/);
    const status = statusMatch?.[1];
    if (error.includes('Insufficient permissions') || error.includes('unauthorized')) {
      return `Sin permisos suficientes (${status}). Verificá que la integración tenga acceso a esta base de datos.`;
    }
  }
  return error;
}
/** Map Notion colors to Tailwind classes */
function getNotionColorClass(color: string, isDark: boolean): string {
  const colors: Record<string, {
    dark: string;
    light: string;
  }> = {
    default: {
      dark: 'bg-[#2F2F2F] text-[rgba(255,255,255,0.8)]',
      light: 'bg-gray-100 text-gray-700'
    },
    gray: {
      dark: 'bg-[#2F2F2F] text-[rgba(255,255,255,0.8)]',
      light: 'bg-gray-100 text-gray-700'
    },
    brown: {
      dark: 'bg-[#4A3219] text-[#C4A381]',
      light: 'bg-[#EFE3D6] text-[#7A4B21]'
    },
    orange: {
      dark: 'bg-[#5C3B00] text-[#D99B38]',
      light: 'bg-[#FADEC9] text-[#9C580B]'
    },
    yellow: {
      dark: 'bg-[#5C4D00] text-[#D9B838]',
      light: 'bg-[#FDECC8] text-[#9C7A0B]'
    },
    green: {
      dark: 'bg-[#1C452A] text-[#63B27D]',
      light: 'bg-[#DBEDDB] text-[#2A7541]'
    },
    blue: {
      dark: 'bg-[#184363] text-[#63A3D9]',
      light: 'bg-[#D3E5EF] text-[#205C8A]'
    },
    purple: {
      dark: 'bg-[#3C235C] text-[#A87CD9]',
      light: 'bg-[#E8DEEE] text-[#5A3286]'
    },
    pink: {
      dark: 'bg-[#5C1C3B] text-[#D9639B]',
      light: 'bg-[#F5E0E9] text-[#9C2A5C]'
    },
    red: {
      dark: 'bg-[#5C1C1C] text-[#D96363]',
      light: 'bg-[#FFE2DD] text-[#9C2A2A]'
    }
  };
  const mapped = colors[color] || colors.default;
  return isDark ? mapped.dark : mapped.light;
}
// ─── Notion Block to HTML Converter ───────────────────────────────────────────
function extractRichText(richTextArray: any[]): string {
  if (!Array.isArray(richTextArray)) return '';
  return richTextArray.map(rt => {
    let text = rt.plain_text || '';
    if (rt.annotations) {
      if (rt.annotations.bold) text = `<strong>${text}</strong>`;
      if (rt.annotations.italic) text = `<em>${text}</em>`;
      if (rt.annotations.strikethrough) text = `<del>${text}</del>`;
      if (rt.annotations.underline) text = `<u>${text}</u>`;
      if (rt.annotations.code) text = `<code>${text}</code>`;
      if (rt.annotations.color && rt.annotations.color !== 'default') {
        text = `<span class="notion-color-${rt.annotations.color}">${text}</span>`;
      }
    }
    if (rt.href) {
      text = `<a href="${rt.href}" target="_blank" rel="noopener noreferrer" class="underline decoration-slate-400 underline-offset-2">${text}</a>`;
    }
    return text;
  }).join('');
}
function notionBlocksToHtml(blocks: any[]): string {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return '<p class="text-[rgba(255,255,255,0.5)] italic text-sm">Página vacía</p>';
  }
  let html = '';
  let inList = false;
  let listType = '';
  const closeList = () => {
    if (inList) {
      html += listType === 'bulleted' ? '</ul>' : '</ol>';
      inList = false;
    }
  };
  for (const block of blocks) {
    const type = block.type;
    const content = block[type];
    if (type === 'bulleted_list_item' || type === 'numbered_list_item') {
      const currentListType = type === 'bulleted_list_item' ? 'bulleted' : 'numbered';
      if (!inList) {
        html += currentListType === 'bulleted' ? '<ul class="list-disc pl-6 my-2 space-y-1">' : '<ol class="list-decimal pl-6 my-2 space-y-1">';
        inList = true;
        listType = currentListType;
      } else if (listType !== currentListType) {
        closeList();
        html += currentListType === 'bulleted' ? '<ul class="list-disc pl-6 my-2 space-y-1">' : '<ol class="list-decimal pl-6 my-2 space-y-1">';
        inList = true;
        listType = currentListType;
      }
    } else {
      closeList();
    }
    switch (type) {
      case 'paragraph':
        const pText = extractRichText(content.rich_text);
        html += pText ? `<p class="my-1 leading-relaxed min-h-[1.5em]">${pText}</p>` : '<p class="my-1 min-h-[1.5em]"></p>';
        break;
      case 'heading_1':
        html += `<h1 class="text-3xl font-bold mt-8 mb-4 leading-tight">${extractRichText(content.rich_text)}</h1>`;
        break;
      case 'heading_2':
        html += `<h2 class="text-2xl font-semibold mt-6 mb-3 leading-tight">${extractRichText(content.rich_text)}</h2>`;
        break;
      case 'heading_3':
        html += `<h3 class="text-xl font-semibold mt-4 mb-2 leading-tight">${extractRichText(content.rich_text)}</h3>`;
        break;
      case 'bulleted_list_item':
      case 'numbered_list_item':
        html += `<li>${extractRichText(content.rich_text)}</li>`;
        break;
      case 'to_do':
        const checked = content.checked ? 'checked' : '';
        html += `<div class="flex items-start gap-2 my-1"><input type="checkbox" disabled ${checked} class="mt-1.5 w-4 h-4 rounded-sm border-gray-400" /><span class="${content.checked ? 'line-through opacity-50' : ''}">${extractRichText(content.rich_text)}</span></div>`;
        break;
      case 'toggle':
        html += `<details class="my-2 group"><summary class="cursor-pointer font-medium list-none flex items-center gap-2"><svg class="w-4 h-4 transition-transform group-open:rotate-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>${extractRichText(content.rich_text)}</summary><div class="pl-6 mt-2 text-sm opacity-80">Contenido anidado (requiere cargar hijos)</div></details>`;
        break;
      case 'code':
        html += `<pre class="bg-[rgba(135,131,120,0.15)] rounded-md p-4 text-sm overflow-x-auto my-4 font-mono"><code>${extractRichText(content.rich_text)}</code></pre>`;
        break;
      case 'quote':
        html += `<blockquote class="border-l-4 border-current pl-4 py-1 my-4 text-lg">${extractRichText(content.rich_text)}</blockquote>`;
        break;
      case 'callout':
        const icon = content.icon?.emoji || '💡';
        html += `<div class="flex gap-3 p-4 bg-[rgba(135,131,120,0.15)] rounded-md my-4"><div class="shrink-0 text-xl">${icon}</div><div class="leading-relaxed">${extractRichText(content.rich_text)}</div></div>`;
        break;
      case 'divider':
        html += `<hr class="border-[rgba(255,255,255,0.1)] my-6" />`;
        break;
      case 'image':
        const imgUrl = content.type === 'external' ? content.external.url : content.file?.url;
        if (imgUrl) {
          html += `<img src="${imgUrl}" alt="Notion Image" class="rounded-md my-4 max-w-full" />`;
        }
        break;
      default:
        html += `<p class="text-[rgba(255,255,255,0.3)] text-xs my-1">[Bloque: ${type}]</p>`;
    }
  }
  closeList();
  return html;
}
// ─── Mock Data ────────────────────────────────────────────────────────────────
const MOCK_DATABASES: NotionDatabase[] = [{
  id: 'db-1',
  title: 'Reading List',
  icon: '📚',
  properties: {
    Name: {
      type: 'title'
    },
    Status: {
      type: 'select'
    },
    Author: {
      type: 'multi_select'
    },
    'Score /5': {
      type: 'select'
    }
  }
}, {
  id: 'db-2',
  title: 'Task List',
  icon: '✅',
  properties: {
    Name: {
      type: 'title'
    },
    Status: {
      type: 'select'
    },
    'Date Created': {
      type: 'created_time'
    }
  }
}];
const MOCK_PAGES: NotionPage[] = [{
  id: 'page-1',
  title: 'The Design of Everyday Things',
  databaseId: 'db-1',
  url: '#',
  lastEdited: '2024-05-10',
  properties: {
    Name: {
      type: 'title',
      title: [{
        plain_text: 'The Design of Everyday Things'
      }]
    },
    Status: {
      type: 'select',
      select: {
        name: 'Reading',
        color: 'blue'
      }
    },
    Author: {
      type: 'multi_select',
      multi_select: [{
        name: 'Don Norman',
        color: 'default'
      }]
    },
    'Score /5': {
      type: 'select',
      select: {
        name: '⭐️⭐️⭐️⭐️⭐️',
        color: 'default'
      }
    }
  }
}, {
  id: 'page-2',
  title: 'Fix navigation bug',
  databaseId: 'db-2',
  url: '#',
  lastEdited: '2024-04-20',
  properties: {
    Name: {
      type: 'title',
      title: [{
        plain_text: 'Fix navigation bug'
      }]
    },
    Status: {
      type: 'select',
      select: {
        name: 'In Progress',
        color: 'yellow'
      }
    }
  }
}];
const MOCK_BLOCKS = [{
  type: 'heading_1',
  heading_1: {
    rich_text: [{
      plain_text: 'Welcome to Notion'
    }]
  }
}, {
  type: 'paragraph',
  paragraph: {
    rich_text: [{
      plain_text: 'This is a mock page to demonstrate the Notion-like rendering.'
    }]
  }
}, {
  type: 'to_do',
  to_do: {
    rich_text: [{
      plain_text: 'Write clean code'
    }],
    checked: true
  }
}, {
  type: 'to_do',
  to_do: {
    rich_text: [{
      plain_text: 'Fix all bugs'
    }],
    checked: false
  }
}];
// ─── Component ────────────────────────────────────────────────────────────────
export function NotionPanel() {
  const isLive = true;
  // Data State
  const [workspaceName, setWorkspaceName] = useState<string>('');
  const [databases, setDatabases] = useState<NotionDatabase[]>([]);
  const [dbsLoading, setDbsLoading] = useState(false);
  const [dbsError, setDbsError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [selectedDb, setSelectedDb] = useState<NotionDatabase | null>(null);
  const [pages, setPages] = useState<NotionPage[]>([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesError, setPagesError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPage, setSelectedPage] = useState<PageDetail | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  // Debug State is now global
  const notionCallDebug = useCallback(async <T = any,>(toolName: string, params: Record<string, any> = {}) => {
    const startTime = performance.now();
    const result = await notionCall<T>(toolName, params);
    const durationMs = Math.round(performance.now() - startTime);
    pushDebugLog({
      source: 'notion',
      tool: toolName,
      params,
      rawResponse: result.rawData || null,
      processedData: result.data || null,
      success: result.success,
      error: result.error,
      durationMs
    });
    return result;
  }, []);
  // UI State
  const [previewWidth, setPreviewWidth] = useState(600);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{
    startX: number;
    startWidth: number;
  } | null>(null);
  const [previewTheme, setPreviewTheme] = useState<PreviewTheme>(() => {
    try {
      return localStorage.getItem('notion-preview-theme') as PreviewTheme || 'dark';
    } catch {
      return 'dark';
    }
  });
  const [systemDark, setSystemDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true);
  const resolvedTheme = previewTheme === 'system' ? systemDark ? 'dark' : 'light' : previewTheme;
  const isDark = resolvedTheme === 'dark';
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const changeTheme = (theme: PreviewTheme) => {
    setPreviewTheme(theme);
    try {
      localStorage.setItem('notion-preview-theme', theme);
    } catch {}
  };
  // Resize Handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeRef.current = {
      startX: e.clientX,
      startWidth: previewWidth
    };
    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const diff = resizeRef.current.startX - ev.clientX;
      const newWidth = Math.min(Math.max(resizeRef.current.startWidth + diff, 400), 1200);
      setPreviewWidth(newWidth);
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      resizeRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [previewWidth]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);
  // ─── API Effects ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function fetchDatabases() {
      setDbsLoading(true);
      setDbsError(null);
      try {
        if (isLive) {
          // 0. Get workspace name from get_bot_user
          try {
            const botRes = await notionCallDebug('get_bot_user');
            if (botRes.success && botRes.data) {
              const wsName = botRes.data?.bot?.workspace_name || botRes.data?.workspace_name || '';
              if (wsName) setWorkspaceName(wsName);
            }
          } catch {}
          // 1. Get database IDs from list_databases
          const listRes = await notionCallDebug('list_databases');
          if (cancelled) return;
          if (!listRes.success) throw new Error(listRes.error || 'Error fetching databases');
          const rawDbs = extractResults(listRes.data);
          // 2. Enrich EVERY database with get_database to get full metadata (title, icon, properties schema)
          const enriched = await Promise.all(rawDbs.map(async (rawDb: any) => {
            try {
              const detailRes = await notionCallDebug('get_database', {
                database_id: rawDb.id
              });
              if (detailRes.success && detailRes.data) {
                const fullDb = detailRes.data;
                return {
                  id: rawDb.id,
                  title: parseDatabaseTitle(fullDb),
                  icon: fullDb.icon?.emoji || fullDb.icon?.external?.url || undefined,
                  properties: fullDb.properties || rawDb.properties || {}
                };
              }
            } catch (e) {
              console.warn('[Notion] get_database failed for', rawDb.id, e);
            }
            // Fallback
            return {
              id: rawDb.id,
              title: parseDatabaseTitle(rawDb),
              icon: rawDb.icon?.emoji || rawDb.icon?.external?.url || undefined,
              properties: rawDb.properties || {}
            };
          }));
          if (cancelled) return;
          setDatabases(enriched);
          if (enriched.length > 0) setSelectedDb(enriched[0]);
        } else {
          await new Promise(r => setTimeout(r, 300));
          if (cancelled) return;
          setDatabases(MOCK_DATABASES);
          setSelectedDb(MOCK_DATABASES[0]);
        }
      } catch (err: any) {
        if (!cancelled) setDbsError(parseApiError(err.message));
      } finally {
        if (!cancelled) setDbsLoading(false);
      }
    }
    fetchDatabases();
    return () => {
      cancelled = true;
    };
  }, [isLive, retryCount, notionCallDebug]);
  useEffect(() => {
    if (!selectedDb || isSearching) return;
    let cancelled = false;
    async function fetchPages() {
      setPagesLoading(true);
      setPagesError(null);
      try {
        if (isLive) {
          const res = await notionCallDebug('query_database', {
            database_id: selectedDb.id
          });
          if (!res.success) {
            const errorStr = res.error || '';
            const isPermissionError = errorStr.includes('Insufficient permissions') || errorStr.includes('unauthorized') || errorStr.includes('403') || errorStr.includes('TOOL_EXECUTION_ERROR');
            if (isPermissionError) {
              // Permission error — don't fall back to list_pages (it also won't have titles)
              // Show a clear message instead
              if (cancelled) return;
              setPages([]);
              setPagesError(`🔒 Esta base de datos no está compartida con la integración.\n\nPara ver las filas, compartí "${selectedDb.title}" con "Workium AI" en Notion:\n1. Abrí la base de datos en Notion\n2. Hacé clic en "..." (menú) → "Connections"\n3. Agregá "Workium AI"`);
              return;
            }
            // Non-permission error — throw to show generic error
            console.warn('[Notion] query_database failed:', errorStr);
            throw new Error(errorStr || 'Error fetching pages');
          }
          if (cancelled) return;
          const results = extractResults(res.data);
          const formattedPages: NotionPage[] = results.map((p: any) => ({
            id: p.id,
            title: parsePageTitle(p),
            lastEdited: p.last_edited_time ? new Date(p.last_edited_time).toLocaleDateString() : undefined,
            databaseId: selectedDb.id,
            url: p.url || `https://notion.so/${p.id?.replace(/-/g, '')}`,
            properties: p.properties || {}
          }));
          setPages(formattedPages);
        } else {
          await new Promise(r => setTimeout(r, 300));
          if (cancelled) return;
          setPages(MOCK_PAGES.filter(p => p.databaseId === selectedDb.id));
        }
      } catch (err: any) {
        if (!cancelled) setPagesError(parseApiError(err.message));
      } finally {
        if (!cancelled) setPagesLoading(false);
      }
    }
    fetchPages();
    return () => {
      cancelled = true;
    };
  }, [selectedDb, isLive, isSearching, notionCallDebug]);
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      clearSearch();
      return;
    }
    setIsSearching(true);
    setPagesLoading(true);
    setPagesError(null);
    setSelectedPage(null);
    try {
      if (isLive) {
        const res = await notionCallDebug('search', {
          query: searchQuery
        });
        if (!res.success) throw new Error(res.error || 'Error searching');
        const results = extractResults(res.data);
        setPages(results.filter((p: any) => p.object === 'page').map((p: any) => ({
          id: p.id,
          title: parsePageTitle(p),
          lastEdited: p.last_edited_time ? new Date(p.last_edited_time).toLocaleDateString() : undefined,
          url: p.url || `https://notion.so/${p.id?.replace(/-/g, '')}`,
          properties: p.properties || {}
        })));
      } else {
        await new Promise(r => setTimeout(r, 300));
        const q = searchQuery.toLowerCase();
        setPages(MOCK_PAGES.filter(p => p.title.toLowerCase().includes(q)));
      }
    } catch (err: any) {
      setPagesError(parseApiError(err.message));
    } finally {
      setPagesLoading(false);
    }
  };
  const clearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    setSelectedPage(null);
  };
  const handlePageClick = async (page: NotionPage) => {
    setSelectedPage({
      ...page,
      contentHtml: ''
    });
    setPageLoading(true);
    setPageError(null);
    try {
      if (isLive) {
        let res = await notionCallDebug('get_block_children', {
          block_id: page.id
        });
        if (res.success && res.data?.results) {
          setSelectedPage({
            ...page,
            contentHtml: notionBlocksToHtml(res.data.results)
          });
        } else {
          console.warn('[Notion] get_block_children failed, trying get_page:', res.error);
          const pageRes = await notionCallDebug('get_page', {
            page_id: page.id
          });
          if (pageRes.success && pageRes.data) {
            // Fallback: render properties as HTML if blocks fail
            let html = '<div class="space-y-4 mt-6">';
            const props = pageRes.data.properties || {};
            for (const key in props) {
              const prop = props[key];
              let value = '';
              if (prop.type === 'title') continue;else if (prop.type === 'rich_text') value = extractAnyText(prop.rich_text);else if (prop.type === 'number') value = prop.number?.toString() || '';else if (prop.type === 'select') value = `<span class="px-2 py-0.5 rounded-sm bg-[rgba(135,131,120,0.15)] text-xs">${prop.select?.name || ''}</span>`;else if (prop.type === 'multi_select') value = (prop.multi_select || []).map((s: any) => `<span class="px-2 py-0.5 rounded-sm bg-[rgba(135,131,120,0.15)] text-xs mr-1">${s.name}</span>`).join('');else if (prop.type === 'date') value = prop.date?.start || '';else if (prop.type === 'checkbox') value = prop.checkbox ? '✅' : '⬜';else if (prop.type === 'url') value = `<a href="${prop.url}" target="_blank" class="underline decoration-[rgba(255,255,255,0.3)]">${prop.url}</a>`;else if (prop.type === 'status') value = `<span class="px-2 py-0.5 rounded-sm bg-[rgba(135,131,120,0.15)] text-xs">${prop.status?.name || ''}</span>`;
              if (value) {
                html += `
                  <div class="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-1 border-b border-[rgba(255,255,255,0.05)] pb-3">
                    <span class="text-[rgba(255,255,255,0.5)] text-sm min-w-[140px] flex items-center gap-1.5">
                      ${key}
                    </span>
                    <span class="text-[rgba(255,255,255,0.9)] text-sm flex-1">${value}</span>
                  </div>`;
              }
            }
            html += '</div>';
            setSelectedPage({
              ...page,
              contentHtml: html || '<p class="text-[rgba(255,255,255,0.5)] italic">Página sin contenido visible.</p>'
            });
          } else {
            const pageErrorStr = pageRes.error || '';
            const isPagePermError = pageErrorStr.includes('Insufficient permissions') || pageErrorStr.includes('TOOL_EXECUTION_ERROR') || pageErrorStr.includes('403');
            setSelectedPage({
              ...page,
              contentHtml: isPagePermError ? `<div class="flex flex-col items-center text-center py-8">
                    <div class="text-4xl mb-4">🔒</div>
                    <p class="text-base font-medium mb-2">Página sin acceso</p>
                    <p class="text-sm opacity-60 max-w-sm">Esta página no está compartida con la integración. Compartila con "Workium AI" en Notion para ver su contenido.</p>
                  </div>` : '<p class="text-[rgba(255,255,255,0.5)] italic">No se pudo cargar el contenido de la página.</p>'
            });
          }
        }
      } else {
        await new Promise(r => setTimeout(r, 400));
        setSelectedPage({
          ...page,
          contentHtml: notionBlocksToHtml(MOCK_BLOCKS)
        });
      }
    } catch (err: any) {
      setPageError(parseApiError(err.message));
    } finally {
      setPageLoading(false);
    }
  };
  // ─── Table Rendering Helpers ──────────────────────────────────────────
  const getTableColumns = () => {
    if (isSearching) return [{
      key: 'title',
      name: 'Name',
      type: 'title'
    }];
    if (!selectedDb || !selectedDb.properties) return [];
    const props = Object.entries(selectedDb.properties);
    // Find the title property
    const titleProp = props.find(([_, schema]) => schema.type === 'title');
    // Get up to 5 other properties
    const otherProps = props.filter(([_, schema]) => schema.type !== 'title').slice(0, 5);
    const cols = [];
    if (titleProp) cols.push({
      key: titleProp[0],
      name: titleProp[0],
      type: 'title'
    });else cols.push({
      key: 'title',
      name: 'Name',
      type: 'title'
    }); // Fallback
    otherProps.forEach(([key, schema]) => {
      cols.push({
        key,
        name: key,
        type: schema.type
      });
    });
    return cols;
  };
  const renderTableCell = (page: NotionPage, col: {
    key: string;
    type: string;
  }) => {
    // Title is special, it might be parsed already or in properties
    if (col.type === 'title') {
      return <div className="flex items-center gap-2 font-medium" data-id="element-5327">
          <FileText size={14} className={isDark ? 'text-[rgba(255,255,255,0.4)]' : 'text-[rgba(55,53,47,0.4)]'} data-id="element-5328" />

          <span className="truncate" data-id="element-5329">{page.title}</span>
        </div>;
    }
    const prop = page.properties?.[col.key];
    if (!prop) return <span className="text-[rgba(255,255,255,0.2)]" data-id="element-5330">-</span>;
    switch (col.type) {
      case 'select':
        if (!prop.select) return null;
        return <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${getNotionColorClass(prop.select.color, isDark)}`} data-id="element-5331">
            {prop.select.name}
          </span>;
      case 'multi_select':
        if (!prop.multi_select || !Array.isArray(prop.multi_select)) return null;
        return <div className="flex flex-wrap gap-1" data-id="element-5332">
            {prop.multi_select.map((s: any, i: number) => <span key={i} className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${getNotionColorClass(s.color, isDark)}`} data-id="element-5333">
                {s.name}
              </span>)}
          </div>;
      case 'status':
        if (!prop.status) return null;
        return <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${getNotionColorClass(prop.status.color, isDark)}`} data-id="element-5334">
            {prop.status.name}
          </span>;
      case 'date':
        if (!prop.date?.start) return null;
        return <span className="whitespace-nowrap" data-id="element-5335">{prop.date.start}</span>;
      case 'url':
        if (!prop.url) return null;
        return <a href={prop.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline truncate max-w-[200px] inline-block" onClick={e => e.stopPropagation()} data-id="element-5336">
            {prop.url.replace(/^https?:\/\//, '')}
          </a>;
      case 'checkbox':
        return prop.checkbox ? <CheckSquare size={14} className="text-blue-500" data-id="element-5337" /> : <div className={`w-3.5 h-3.5 border rounded-sm ${isDark ? 'border-[rgba(255,255,255,0.2)]' : 'border-[rgba(55,53,47,0.2)]'}`} data-id="element-5338" />;
      case 'rich_text':
        return <span className="truncate max-w-[250px] inline-block" data-id="element-5339">
            {extractAnyText(prop.rich_text)}
          </span>;
      case 'created_time':
        return <span className="whitespace-nowrap" data-id="element-5340">
            {new Date(prop.created_time).toLocaleDateString()}
          </span>;
      default:
        return <span className="truncate text-[rgba(255,255,255,0.4)] text-xs" data-id="element-5341">
            {col.type}
          </span>;
    }
  };
  const getPropertyIcon = (type: string) => {
    switch (type) {
      case 'title':
        return <Type size={14} data-id="element-5342" />;
      case 'rich_text':
        return <AlignLeft size={14} data-id="element-5343" />;
      case 'number':
        return <Hash size={14} data-id="element-5344" />;
      case 'select':
      case 'multi_select':
      case 'status':
        return <List size={14} data-id="element-5345" />;
      case 'date':
      case 'created_time':
        return <CalendarIcon size={14} data-id="element-5346" />;
      case 'checkbox':
        return <CheckSquare size={14} data-id="element-5347" />;
      case 'url':
        return <LinkIcon size={14} data-id="element-5348" />;
      default:
        return <MoreHorizontal size={14} data-id="element-5349" />;
    }
  };
  // ─── Render ───────────────────────────────────────────────────────────
  const showPreview = !!selectedPage;
  const tableCols = getTableColumns();
  const themeClasses = isDark ? {
    bgMain: 'bg-[#191919]',
    bgSidebar: 'bg-[#202020]',
    bgHover: 'hover:bg-[#2F2F2F]',
    bgActive: 'bg-[#2F2F2F]',
    border: 'border-[#2F2F2F]',
    textPrimary: 'text-[rgba(255,255,255,0.9)]',
    textSecondary: 'text-[rgba(255,255,255,0.5)]',
    inputBg: 'bg-[#202020]'
  } : {
    bgMain: 'bg-[#FFFFFF]',
    bgSidebar: 'bg-[#F7F7F5]',
    bgHover: 'hover:bg-[#EFEFED]',
    bgActive: 'bg-[#EFEFED]',
    border: 'border-[#E9E9E7]',
    textPrimary: 'text-[#37352F]',
    textSecondary: 'text-[rgba(55,53,47,0.6)]',
    inputBg: 'bg-[#F7F7F5]'
  };
  return <div className={`flex-1 flex h-full overflow-hidden ${themeClasses.bgMain} ${themeClasses.textPrimary} font-sans transition-colors duration-200`} data-id="element-5350">
      {/* ── LEFT SIDEBAR: Databases ── */}
      {!isFullscreen && <div className={`w-60 ${themeClasses.border} border-r ${themeClasses.bgSidebar} flex flex-col shrink-0 transition-colors duration-200`} data-id="element-5351">
          <div className="p-4 pb-2" data-id="element-5352">
            <div className={`text-xs font-semibold ${themeClasses.textSecondary} uppercase tracking-wider mb-1 px-2`} data-id="element-5353">
              Workspace
            </div>
            {workspaceName && <div className={`text-sm font-medium px-2 mb-2 truncate ${themeClasses.textPrimary}`} title={workspaceName} data-id="element-5354">
                {workspaceName}
              </div>}
          </div>
          <div className="flex-1 overflow-y-auto px-2 space-y-0.5" data-id="element-5355">
            {dbsLoading ? Array.from({
          length: 5
        }).map((_, i) => <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-md animate-pulse" data-id="element-5356">
                  <div className={`w-4 h-4 rounded ${isDark ? 'bg-[#2F2F2F]' : 'bg-[#E9E9E7]'} shrink-0`} data-id="element-5357" />

                  <div className={`h-3 ${isDark ? 'bg-[#2F2F2F]' : 'bg-[#E9E9E7]'} rounded w-2/3`} data-id="element-5358" />
                </div>) : dbsError ? <div className="px-3 py-3 text-xs text-red-400 bg-red-500/10 rounded-md m-2" data-id="element-5359">
                <AlertCircle size={14} className="mb-1.5" data-id="element-5360" />
                <p className="mb-2 opacity-90" data-id="element-5361">{dbsError}</p>
                <button onClick={() => setRetryCount(c => c + 1)} className="px-2 py-1 bg-red-500/20 rounded text-xs font-medium hover:bg-red-500/30 transition-colors w-full" data-id="element-5362">
                  Reintentar
                </button>
              </div> : databases.length === 0 ? <div className={`px-3 py-2 text-xs ${themeClasses.textSecondary}`} data-id="element-5363">
                No hay bases de datos.
              </div> : databases.map(db => <button key={db.id} onClick={() => {
          setSelectedDb(db);
          setIsSearching(false);
          setSearchQuery('');
          setSelectedPage(null);
          setIsFullscreen(false);
        }} className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-left transition-colors text-sm ${selectedDb?.id === db.id && !isSearching ? `${themeClasses.bgActive} font-medium` : `${themeClasses.bgHover} ${themeClasses.textSecondary} hover:${themeClasses.textPrimary}`}`} data-id="element-5364">
                  <span className="shrink-0 text-base flex items-center justify-center w-5" data-id="element-5365">
                    {db.icon || <Book size={15} className="opacity-70" data-id="element-5366" />}
                  </span>
                  <span className="truncate" data-id="element-5367">{db.title}</span>
                </button>)}
          </div>
        </div>}

      {/* ── CENTER: Table View ── */}
      {!isFullscreen && <div className="flex-1 flex flex-col min-w-0" data-id="element-5368">
          <div className={`px-8 py-6 shrink-0 border-b ${themeClasses.border}`} data-id="element-5369">
            <div className="max-w-5xl" data-id="element-5370">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2" data-id="element-5371">
                {isSearching ? <>
                    <Search size={24} className={themeClasses.textSecondary} data-id="element-5372" />
                    Resultados de búsqueda
                  </> : selectedDb ? <>
                    <span className="text-2xl" data-id="element-5373">
                      {selectedDb.icon || <Book size={24} className={themeClasses.textSecondary} data-id="element-5374" />}
                    </span>
                    {selectedDb.title}
                  </> : 'Notion'}
              </h2>

              <div className="flex items-center gap-3 max-w-2xl" data-id="element-5375">
                <form onSubmit={handleSearch} className="relative flex-1" data-id="element-5376">
                  <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`} data-id="element-5377" />

                  <input type="text" placeholder="Buscar páginas..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full ${themeClasses.inputBg} border ${themeClasses.border} rounded-md pl-9 pr-10 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all`} data-id="element-5378" />

                  {searchQuery && <button type="button" onClick={clearSearch} className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-sm ${themeClasses.bgHover} ${themeClasses.textSecondary}`} data-id="element-5379">
                      <X size={14} data-id="element-5380" />
                    </button>}
                </form>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto" data-id="element-5381">
            {pagesLoading ? <div className="p-8 space-y-4" data-id="element-5382">
                {Array.from({
            length: 5
          }).map((_, i) => <div key={i} className={`h-10 rounded-md ${isDark ? 'bg-[#2F2F2F]' : 'bg-[#E9E9E7]'} animate-pulse w-full max-w-4xl`} data-id="element-5383" />)}
              </div> : pagesError ? <div className="p-8 flex flex-col items-center justify-center text-center mt-10 max-w-lg mx-auto" data-id="element-5384">
                {pagesError.includes('🔒') ? <>
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mb-4 ${isDark ? 'bg-[#2F2F2F]' : 'bg-[#F7F7F5]'}`} data-id="element-5385">
                      🔒
                    </div>
                    <h3 className="text-base font-semibold mb-2" data-id="element-5386">
                      Base de datos privada
                    </h3>
                    <div className={`text-sm ${themeClasses.textSecondary} space-y-2 mb-6`} data-id="element-5387">
                      <p data-id="element-5388">
                        Para ver las filas de{' '}
                        <strong data-id="element-5389">"{selectedDb?.title}"</strong>, compartila con
                        la integración:
                      </p>
                      <ol className={`text-left space-y-1.5 mt-3 ${isDark ? 'bg-[#2F2F2F]' : 'bg-[#F7F7F5]'} rounded-lg p-4`} data-id="element-5390">
                        <li className="flex items-start gap-2" data-id="element-5391">
                          <span className="font-bold shrink-0" data-id="element-5392">1.</span> Abrí la
                          base de datos en Notion
                        </li>
                        <li className="flex items-start gap-2" data-id="element-5393">
                          <span className="font-bold shrink-0" data-id="element-5394">2.</span> Hacé
                          clic en <strong data-id="element-5395">"..."</strong> (menú) →{' '}
                          <strong data-id="element-5396">"Connections"</strong>
                        </li>
                        <li className="flex items-start gap-2" data-id="element-5397">
                          <span className="font-bold shrink-0" data-id="element-5398">3.</span> Agregá{' '}
                          <strong data-id="element-5399">"Workium AI"</strong>
                        </li>
                      </ol>
                    </div>
                    <button onClick={() => {
              setPagesError(null);
              setPagesLoading(true);
              setRetryCount(c => c + 1);
            }} className={`px-4 py-2 ${isDark ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md text-sm font-medium transition-colors`} data-id="element-5400">
                      Ya lo compartí, reintentar
                    </button>
                  </> : <>
                    <AlertCircle size={24} className="text-red-400 mb-3" data-id="element-5401" />
                    <p className="text-sm text-red-400 mb-4 max-w-md" data-id="element-5402">
                      {pagesError}
                    </p>
                    <button onClick={() => {
              if (selectedDb) {
                setPagesError(null);
                setRetryCount(c => c + 1);
              }
            }} className={`px-4 py-2 ${themeClasses.bgSidebar} ${themeClasses.border} border rounded-md text-sm font-medium ${themeClasses.bgHover} transition-colors`} data-id="element-5403">
                      Reintentar
                    </button>
                  </>}
              </div> : pages.length === 0 ? <div className="py-20 flex flex-col items-center justify-center text-center" data-id="element-5404">
                <FileText size={32} className={`${themeClasses.textSecondary} mb-4 opacity-50`} data-id="element-5405" />

                <p className={`text-sm ${themeClasses.textSecondary}`} data-id="element-5406">
                  {isSearching ? 'No se encontraron páginas.' : 'Esta base de datos está vacía.'}
                </p>
              </div> : <table className="w-full text-left border-collapse whitespace-nowrap" data-id="element-5407">
                <thead data-id="element-5408">
                  <tr data-id="element-5409">
                    {tableCols.map((col, i) => <th key={col.key} className={`sticky top-0 z-10 px-4 py-2 font-normal text-xs ${themeClasses.textSecondary} border-b border-r ${themeClasses.border} ${themeClasses.bgMain} ${i === 0 ? 'pl-8 w-[300px]' : ''}`} data-id="element-5410">
                        <div className="flex items-center gap-1.5" data-id="element-5411">
                          <span className="opacity-70" data-id="element-5412">
                            {getPropertyIcon(col.type)}
                          </span>
                          {col.name}
                        </div>
                      </th>)}
                  </tr>
                </thead>
                <tbody data-id="element-5413">
                  {pages.map(page => <tr key={page.id} onClick={() => handlePageClick(page)} className={`cursor-pointer border-b ${themeClasses.border} ${selectedPage?.id === page.id ? themeClasses.bgActive : themeClasses.bgHover} transition-colors group`} data-id="element-5414">
                      {tableCols.map((col, i) => <td key={col.key} className={`px-4 py-2.5 text-sm border-r ${themeClasses.border} ${i === 0 ? 'pl-8' : ''}`} data-id="element-5415">
                          {renderTableCell(page, col)}
                        </td>)}
                    </tr>)}
                </tbody>
              </table>}
          </div>
        </div>}

      {/* ── RIGHT PANEL: Page Preview ── */}
      {showPreview && <>
          {!isFullscreen && <div onMouseDown={handleResizeStart} className={`w-1 shrink-0 cursor-col-resize group relative transition-colors ${isResizing ? 'bg-blue-500/50' : `hover:bg-blue-500/30 ${themeClasses.border} border-l`}`} data-id="element-5416">
              <div className="absolute inset-y-0 -left-2 -right-2 z-10" data-id="element-5417" />
            </div>}

          <div style={{
        width: isFullscreen ? '100%' : `${previewWidth}px`
      }} className={`flex flex-col shrink-0 transition-all duration-200 ${isFullscreen ? 'absolute inset-0 z-50' : 'relative border-l ' + themeClasses.border} ${themeClasses.bgMain}`} data-id="element-5418">
            {/* Preview Header */}
            <div className={`px-6 py-3 border-b ${themeClasses.border} flex items-center justify-between sticky top-0 z-10 ${themeClasses.bgMain}`} data-id="element-5419">
              <div className="flex items-center gap-2 text-xs font-medium truncate pr-4" data-id="element-5420">
                <span className={themeClasses.textSecondary} data-id="element-5421">
                  {selectedDb?.title || 'Búsqueda'}
                </span>
                <ChevronRight size={12} className={themeClasses.textSecondary} data-id="element-5422" />

                <span className="truncate" data-id="element-5423">{selectedPage.title}</span>
              </div>

              <div className="flex items-center gap-1 shrink-0" data-id="element-5424">
                {!pageLoading && !pageError && <SummarizeButton content={selectedPage.contentHtml} title={selectedPage.title} contextType="página de Notion" variant="header" className="mr-2" data-id="element-5425" />}

                <div className={`flex items-center rounded-md p-0.5 mr-2 ${themeClasses.bgSidebar}`} data-id="element-5426">
                  <button onClick={() => changeTheme('light')} className={`p-1.5 rounded-sm transition-all ${!isDark && previewTheme !== 'system' ? 'bg-white shadow-sm text-amber-500' : themeClasses.textSecondary}`} title="Claro" data-id="element-5427">
                    <Sun size={14} data-id="element-5428" />
                  </button>
                  <button onClick={() => changeTheme('dark')} className={`p-1.5 rounded-sm transition-all ${isDark && previewTheme !== 'system' ? 'bg-[#2F2F2F] shadow-sm text-blue-400' : themeClasses.textSecondary}`} title="Oscuro" data-id="element-5429">
                    <Moon size={14} data-id="element-5430" />
                  </button>
                  <button onClick={() => changeTheme('system')} className={`p-1.5 rounded-sm transition-all ${previewTheme === 'system' ? isDark ? 'bg-[#2F2F2F] text-emerald-400' : 'bg-white text-emerald-500' : themeClasses.textSecondary}`} title="Sistema" data-id="element-5431">
                    <Monitor size={14} data-id="element-5432" />
                  </button>
                </div>

                <button onClick={() => setIsFullscreen(!isFullscreen)} className={`p-1.5 rounded-md ${themeClasses.bgHover} ${themeClasses.textSecondary} hover:${themeClasses.textPrimary} transition-colors`} data-id="element-5433">
                  {isFullscreen ? <Minimize2 size={16} data-id="element-5434" /> : <Maximize2 size={16} data-id="element-5435" />}
                </button>
                {selectedPage.url && <a href={selectedPage.url} target="_blank" rel="noopener noreferrer" className={`p-1.5 rounded-md ${themeClasses.bgHover} ${themeClasses.textSecondary} hover:${themeClasses.textPrimary} transition-colors`} data-id="element-5436">
                    <ExternalLink size={16} data-id="element-5437" />
                  </a>}
                <button onClick={() => {
              setSelectedPage(null);
              setIsFullscreen(false);
            }} className={`p-1.5 rounded-md ${themeClasses.bgHover} ${themeClasses.textSecondary} hover:${themeClasses.textPrimary} transition-colors`} data-id="element-5438">
                  <X size={16} data-id="element-5439" />
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div className={`flex-1 overflow-y-auto custom-scrollbar relative ${isFullscreen ? 'px-8 py-12 max-w-4xl mx-auto w-full' : 'px-8 py-10'}`} data-id="element-5440">
              {pageLoading ? <div className="space-y-6 animate-pulse max-w-2xl" data-id="element-5441">
                  <div className={`h-10 rounded-md w-3/4 ${isDark ? 'bg-[#2F2F2F]' : 'bg-[#E9E9E7]'}`} data-id="element-5442" />

                  <div className="space-y-3 pt-4" data-id="element-5443">
                    <div className={`h-4 rounded w-full ${isDark ? 'bg-[#2F2F2F]' : 'bg-[#E9E9E7]'}`} data-id="element-5444" />

                    <div className={`h-4 rounded w-5/6 ${isDark ? 'bg-[#2F2F2F]' : 'bg-[#E9E9E7]'}`} data-id="element-5445" />

                    <div className={`h-4 rounded w-4/6 ${isDark ? 'bg-[#2F2F2F]' : 'bg-[#E9E9E7]'}`} data-id="element-5446" />
                  </div>
                </div> : pageError ? <div className="text-center py-12" data-id="element-5447">
                  <AlertCircle size={24} className="mx-auto text-red-400 mb-3" data-id="element-5448" />

                  <p className="text-sm text-red-400" data-id="element-5449">{pageError}</p>
                </div> : <div className="max-w-3xl" data-id="element-5450">
                  <h1 className="text-4xl font-bold mb-8 leading-tight" data-id="element-5451">
                    {selectedPage.title}
                  </h1>

                  <div className={`notion-content text-base leading-relaxed ${isDark ? 'text-[rgba(255,255,255,0.8)]' : 'text-[#37352F]'}`} dangerouslySetInnerHTML={{
              __html: selectedPage.contentHtml
            }} data-id="element-5452" />
                </div>}
            </div>
          </div>
        </>}
    </div>;
}